import React from 'react'
import { CONTACT_EMAIL } from '../../Constants'
import LegalPageLayout from './LegalPageLayout'

const sections = [
  {
    title: '1. Information We Collect',
    blocks: [
      {
        type: 'paragraph',
        text: 'We may collect information you provide directly to us, including your name, username, email address, profile image, event responses, guest display name, merchant or venue details, support messages, and any content you choose to upload or submit through Anear.'
      },
      {
        type: 'paragraph',
        text: 'We also collect technical and usage information when you use Anear, including device and browser details, IP address, timestamps, diagnostics, event participation history, participation status, actions taken during an experience, and related service metadata.'
      },
      {
        type: 'paragraph',
        text: 'Because Anear supports in-person, location-aware experiences, we may collect precise or approximate location information when needed to verify proximity to an event, enable venue access, or enforce participation-radius rules.'
      },
      {
        type: 'paragraph',
        text: 'We may use cookies, local storage, and similar technologies to keep you signed in, preserve guest sessions, remember preferences, maintain security, and improve the service.'
      }
    ]
  },
  {
    title: '2. How We Use Information',
    blocks: [
      {
        type: 'list',
        items: [
          'Provide, operate, maintain, and improve Anear.',
          'Create and manage accounts, guest sessions, events, venues, merchant tools, and developer tools.',
          'Enable users to join, host, and participate in live or self-paced experiences.',
          'Verify eligibility or proximity for location-based features.',
          'Personalize participant identity, event state, and role-based views.',
          'Communicate with you about product updates, support, security, and administrative matters.',
          'Detect, investigate, and prevent fraud, abuse, misuse, and violations of our terms.',
          'Comply with legal obligations and enforce our agreements.'
        ]
      }
    ]
  },
  {
    title: '3. How We Share Information',
    blocks: [
      {
        type: 'paragraph',
        text: 'Some information is visible to other users as part of the live experience. For example, your display name, avatar, participation status, event actions, or submitted content may be shown to participants, hosts, or spectators where relevant to the experience.'
      },
      {
        type: 'paragraph',
        text: 'If you join an event operated by a merchant, venue, organizer, or host, we may share information related to your participation with that operator so they can run the experience.'
      },
      {
        type: 'paragraph',
        text: 'We may share information with service providers that help us operate Anear, such as hosting, storage, real-time messaging, analytics, communications, customer support, and payment providers.'
      },
      {
        type: 'paragraph',
        text: 'We may also disclose information when reasonably necessary to comply with law, enforce our policies, protect rights or safety, or in connection with a merger, acquisition, financing, restructuring, bankruptcy, or asset sale.'
      }
    ]
  },
  {
    title: '4. Retention',
    blocks: [
      {
        type: 'paragraph',
        text: 'We retain personal information for as long as reasonably necessary to provide the service, maintain security, resolve disputes, enforce our agreements, and comply with legal obligations. Guest sessions, cookies, and event records may persist for limited periods based on operational and fraud-prevention needs.'
      }
    ]
  },
  {
    title: '5. Your Choices',
    blocks: [
      {
        type: 'paragraph',
        text: 'Depending on your location and applicable law, you may have the right to request access to, correction of, or deletion of your personal information, or to object to or restrict certain processing.'
      },
      {
        type: 'paragraph',
        text: 'You may also control some information directly by updating your profile, adjusting browser or device location permissions, clearing cookies or local storage, or choosing not to participate in location-enabled experiences.'
      },
      {
        type: 'paragraph',
        text: `To make a privacy request, email us at ${CONTACT_EMAIL}.`
      }
    ]
  },
  {
    title: '6. Children',
    blocks: [
      {
        type: 'paragraph',
        text: 'Anear is not intended for children under 13, and we do not knowingly collect personal information from children under 13 without appropriate authorization. If you believe a child has provided information in violation of this policy, please contact us so we can investigate.'
      }
    ]
  },
  {
    title: '7. Security',
    blocks: [
      {
        type: 'paragraph',
        text: 'We use reasonable administrative, technical, and organizational safeguards designed to protect personal information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.'
      }
    ]
  },
  {
    title: '8. International Use',
    blocks: [
      {
        type: 'paragraph',
        text: 'Anear may be hosted and operated in countries other than the one in which you reside. By using the service, you understand that your information may be processed in jurisdictions that may have different data protection laws than your own.'
      }
    ]
  },
  {
    title: '9. Changes to This Policy',
    blocks: [
      {
        type: 'paragraph',
        text: 'We may update this Privacy Policy from time to time. If we make material changes, we will post the updated version here and update the effective date. Your continued use of Anear after the revised policy becomes effective means the updated policy will apply to your use of the service.'
      }
    ]
  }
]

const PrivacyPolicyPage = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      effectiveDate="March 23, 2026"
      intro="Anear.me, operated by Anear Corporation, provides location-aware, in-person interactive experiences, including live events, games, social activities, and related tools for participants, hosts, merchants, and developers. This Privacy Policy explains what information we collect, how we use it, when we share it, and the choices available to you."
      sections={sections}
      contactEmail={CONTACT_EMAIL}
      companyName="Anear Corporation"
    />
  )
}

export default PrivacyPolicyPage
